import React from "react";
import type { SelectProps } from "@mui/joy";
import { Select } from "@mui/joy";
import { unstable_useForkRef as useForkRef } from "@mui/utils";
import { colorTokens } from "../../styles/globalStyles";

function sanitizeListboxId(reactId: string): string {
  return `dark-select-${reactId.replace(/:/g, "")}`;
}

const DarkSelect = React.forwardRef<HTMLButtonElement, SelectProps<any, false>>((props, ref) => {
  const {
    sx,
    slotProps,
    listboxOpen: listboxOpenProp,
    defaultListboxOpen = false,
    onListboxOpenChange,
    listboxId: listboxIdProp,
    ...other
  } = props;

  const reactId = React.useId();
  const fallbackListboxId = React.useMemo(() => sanitizeListboxId(reactId), [reactId]);
  const listboxDomId = listboxIdProp ?? fallbackListboxId;

  const isListboxControlled = listboxOpenProp !== undefined;
  const [internalListboxOpen, setInternalListboxOpen] = React.useState(Boolean(defaultListboxOpen));
  const listboxOpen = isListboxControlled ? Boolean(listboxOpenProp) : internalListboxOpen;

  const rootRef = React.useRef<HTMLElement | null>(null);
  const handleRootRef = useForkRef(ref, rootRef);

  const handleListboxOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!isListboxControlled) {
        setInternalListboxOpen(nextOpen);
      }
      onListboxOpenChange?.(nextOpen);
    },
    [isListboxControlled, onListboxOpenChange],
  );

  React.useEffect(() => {
    if (!listboxOpen || typeof document === "undefined") {
      return undefined;
    }

    const onDocumentMouseDownCapture = (ev: MouseEvent) => {
      const target = ev.target;
      if (target == null || !(target instanceof Node)) {
        return;
      }

      if (rootRef.current?.contains(target)) {
        return;
      }

      const listRoot = document.getElementById(listboxDomId);
      if (listRoot?.contains(target)) {
        return;
      }

      handleListboxOpenChange(false);
    };

    document.addEventListener("mousedown", onDocumentMouseDownCapture, true);
    return () => document.removeEventListener("mousedown", onDocumentMouseDownCapture, true);
  }, [listboxOpen, listboxDomId, handleListboxOpenChange]);

  const darkSelectStyles = {
    backgroundColor: colorTokens.bg.surfaceMuted,
    color: colorTokens.text.primary,
    borderRadius: "10px",
    border: `1px solid ${colorTokens.border.strong}`,
    fontSize: { xs: "0.8rem", sm: "0.875rem" },
    minHeight: { xs: 32, sm: 36 },
    "&:hover": {
      borderColor: colorTokens.border.focus,
      backgroundColor: colorTokens.bg.hover,
    },
    "&:focus": {
      borderColor: colorTokens.border.focus,
      color: colorTokens.text.primary,
    },
    ...sx,
  };

  const darkListboxStyles = {
    zIndex: 10000,
    backgroundColor: `${colorTokens.bg.surfaceMuted} !important`,
    border: `1px solid ${colorTokens.border.default}`,
    maxHeight: { xs: "35vh", sm: "40vh" },
    overflowY: "auto",
    "& .MuiOption-root": {
      backgroundColor: `${colorTokens.bg.surfaceMuted} !important`,
      color: `${colorTokens.text.primary} !important`,
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
      minHeight: { xs: 36, sm: 40 },
      "&:hover": {
        backgroundColor: `${colorTokens.bg.hover} !important`,
        color: `${colorTokens.text.primary} !important`,
      },
      "&.Mui-selected": {
        backgroundColor: `${colorTokens.accent.primary} !important`,
        color: `${colorTokens.accent.contrastText} !important`,
        "&:hover": {
          backgroundColor: `${colorTokens.accent.hover} !important`,
          color: `${colorTokens.accent.contrastText} !important`,
        },
      },
    },
    "& li": {
      backgroundColor: `${colorTokens.bg.surfaceMuted} !important`,
      color: `${colorTokens.text.primary} !important`,
    },
    "& [role='option']": {
      backgroundColor: `${colorTokens.bg.surfaceMuted} !important`,
      color: `${colorTokens.text.primary} !important`,
    },
    "& [data-color-swatch]": {
      backgroundColor: "var(--swatch-color) !important",
    },
  };

  return (
    <Select
      ref={handleRootRef}
      listboxId={listboxDomId}
      listboxOpen={listboxOpen}
      onListboxOpenChange={handleListboxOpenChange}
      sx={darkSelectStyles}
      slotProps={{
        ...slotProps,
        listbox: {
          sx: darkListboxStyles,
          disablePortal: false,
          modifiers: [
            {
              name: "preventOverflow",
              enabled: true,
              options: { boundary: "viewport", padding: 8 },
            },
            {
              name: "flip",
              enabled: true,
              options: { fallbackPlacements: ["top", "bottom"] },
            },
          ],
          ...slotProps?.listbox,
        } as any,
      }}
      {...other}
    />
  );
});

DarkSelect.displayName = "DarkSelect";

export default DarkSelect;
